// Weight- and price-embedded barcodes (scale labels), decoded exactly the way
// Red Fire ISE's register does (inventory.service getItemsByBarcode) so a
// label reads the same on a register and on a transfer.
//
// A client configures parsers as `settings.barcodeParsers`:
//   { format: '2AAAAAPPPPPC', getQtyFromPrice: true, includeDefinedNumbers: true }
//   { format: '4AAAAAWWwwwC', getQtyFromPrice: false, includeDefinedNumbers: true }
// Reserved format characters: A item code · P price (cents) · W weight before
// the decimal · w weight after · C check digit (not validated) · I ignore.
// Any other character is a literal that must match the scanned digit; with
// includeDefinedNumbers those literals are part of the stored item code
// ("2" + "00043" → the account's barcode entry "200043").

export interface BarcodeParser {
  format: string;
  getQtyFromPrice?: boolean;
  includeDefinedNumbers?: boolean;
}

export interface DecodedBarcode {
  raw: string;
  itemCode: string;
  price: number | null;   // cents
  qty: number | null;     // decoded weight/quantity
  parser: BarcodeParser;
}

const RESERVED = ['A', 'P', 'W', 'C', 'p', 'w', 'I'];

export function parseEncodedBarcode(barcode: string, parsers: BarcodeParser[]): DecodedBarcode | null {
  const code = barcode == null ? '' : String(barcode);
  if (!code || !Array.isArray(parsers)) {
    return null;
  }
  for (const parser of parsers) {
    if (!parser || !parser.format || code.length !== parser.format.length) {
      continue;
    }
    let match = true;
    let itemCode = '';
    let priceCode = '';
    let qtyCodePre = '';
    let qtyCodePost = '';
    for (let i = 0; i < parser.format.length; i++) {
      const f = parser.format[i];
      const isReserved = RESERVED.indexOf(f) !== -1;
      if (!isReserved && f !== code[i]) {
        match = false;
        break;
      }
      switch (f) {
        case 'A': itemCode += code[i]; break;
        case 'P': priceCode += code[i]; break;
        case 'p': priceCode += code[i]; break;
        case 'W': qtyCodePre += code[i]; break;
        case 'w': qtyCodePost += code[i]; break;
        default:
          if (parser.includeDefinedNumbers && !isReserved && f >= '0' && f <= '9') {
            itemCode += code[i];
          }
          break;
      }
    }
    if (!match) {
      continue;
    }
    let price: number | null = null;
    if (priceCode.length > 0) {
      price = parseInt(priceCode, 10);
    }
    let qty: number | null = null;
    if (qtyCodePre.length > 0 || qtyCodePost.length > 0) {
      qty = (qtyCodePost.length === 0 || parseInt(qtyCodePost, 10) === 0)
        ? parseInt(qtyCodePre || '0', 10)
        : parseFloat((qtyCodePre || '0') + '.' + qtyCodePost);
    }
    return { raw: code, itemCode, price, qty, parser };
  }
  return null;
}

// Quantity a decoded label represents for an item priced at `unitPrice`
// cents: the decoded weight, or (price-embedded labels) price ÷ unit price
// to three decimals, as the register does. Null when it cannot be derived.
export function decodedQuantity(decoded: DecodedBarcode, unitPrice: number): number | null {
  if (!decoded) {
    return null;
  }
  if (decoded.parser.getQtyFromPrice) {
    if (decoded.price == null || !unitPrice || unitPrice <= 0) {
      return null;
    }
    return +(decoded.price / unitPrice).toFixed(3);
  }
  return decoded.qty != null ? +decoded.qty.toFixed(3) : null;
}
